<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body class="mb-1">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">DOSES ALREADY GIVEN</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <b-form @submit.prevent="saveDoses">
              <p class="text-lg text-black font-semibold">
                Enter date and time for single dose or starting dose
              </p>
              <DateTimeInput
                id="simStartDT"
                :field="fieldName"
                @datetime-event="initialDoseDateTimeValue"
              />
              <hr />
              <p class="text-lg text-black font-semibold">Add a single or loading dose</p>
              <b-input-group>
                <label v-b-tooltip.hover="'Dose amount given (single or loading dose)'">Dose</label>
                <VField
                  v-slot="{ meta }"
                  v-model="form.initialDose.amount"
                  name="initialDose"
                  :rules="doseRules"
                >
                  <b-form-input
                    id="initDose"
                    v-model="form.initialDose.amount"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    placeholder="Amount"
                    :state="getValidationState(meta)"
                  />
                </VField>
                <b-form-select
                  id="initDoseUnit"
                  ref="doseUnit"
                  v-model="form.initialDose.amountUnit"
                  class="unit-dropdown"
                  :options="units.dose"
                  :clearable="false"
                >
                </b-form-select>
              </b-input-group>
              <VErrorMessage name="initialDose" as="div" class="invalid-feedback d-block" />
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Length of the IV infusion — typically 1–2 hours for vancomycin'
                  "
                  >Duration</label
                >
                <VField
                  v-slot="{ meta }"
                  v-model="form.initialDose.duration"
                  name="initialDuration"
                  :rules="durationRules"
                >
                  <b-form-input
                    id="initDuration"
                    v-model="form.initialDose.duration"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    placeholder="Infusion duration"
                    :state="getValidationState(meta)"
                  />
                </VField>
                <b-form-select
                  id="initDurationUnit"
                  ref="durationUnit"
                  v-model="form.initialDose.durationUnit"
                  class="unit-dropdown"
                  :options="units.duration"
                  :clearable="false"
                >
                </b-form-select>
              </b-input-group>
              <VErrorMessage name="initialDuration" as="div" class="invalid-feedback d-block" />
              <div v-if="deliveryMethod !== 'CONT'">
                <hr />
                <p class="text-lg text-black font-semibold">Add a sequence of doses</p>
                <b-input-group>
                  <label v-b-tooltip.hover="'Dose amount for each repeated dose in the sequence'"
                    >Dose</label
                  >
                  <VField
                    v-slot="{ meta }"
                    v-model="form.subDose.amount"
                    name="subDose"
                    :rules="subdoseRules"
                  >
                    <b-form-input
                      id="subDose"
                      v-model="form.subDose.amount"
                      type="number"
                      :number="true"
                      class="input-with-unit"
                      placeholder="Amount"
                      :state="getValidationState(meta)"
                    />
                  </VField>
                  <b-form-select
                    id="subDoseUnit"
                    ref="subdoseUnit"
                    v-model="form.subDose.amountUnit"
                    class="unit-dropdown"
                    :options="units.dose"
                    :clearable="false"
                  >
                  </b-form-select>
                </b-input-group>
                <VErrorMessage name="subDose" as="div" class="invalid-feedback d-block" />
                <b-input-group>
                  <label v-b-tooltip.hover="'Length of each infusion in the sequence'"
                    >Duration</label
                  >
                  <VField
                    v-slot="{ meta }"
                    v-model="form.subDose.duration"
                    name="subDuration"
                    :rules="subdurationRules"
                  >
                    <b-form-input
                      id="subDuration"
                      v-model="form.subDose.duration"
                      type="number"
                      :number="true"
                      class="input-with-unit"
                      placeholder="Infusion duration"
                      :state="getValidationState(meta)"
                    />
                  </VField>
                  <b-form-select
                    id="subDurationUnit"
                    v-model="form.subDose.durationUnit"
                    class="unit-dropdown"
                    :options="units.duration"
                    :clearable="false"
                  >
                  </b-form-select>
                </b-input-group>
                <VErrorMessage name="subDuration" as="div" class="invalid-feedback d-block" />
                <b-input-group>
                  <label
                    v-b-tooltip.hover="
                      'Time between each dose in the sequence (e.g. every 8, 12, or 24 hours)'
                    "
                    >Interval</label
                  >
                  <VField
                    v-slot="{ meta }"
                    v-model="form.subDose.interval"
                    name="subInterval"
                    :rules="{ intervalBetween: true }"
                  >
                    <b-form-input
                      id="subInterval"
                      v-model="form.subDose.interval"
                      type="number"
                      :number="true"
                      class="input-with-unit"
                      placeholder="Dosing interval"
                      :state="getValidationState(meta)"
                    />
                  </VField>
                  <b-form-select
                    id="subIntervalUnit"
                    v-model="form.subDose.intervalUnit"
                    class="unit-dropdown"
                    :options="units.interval"
                    :clearable="false"
                  >
                  </b-form-select>
                </b-input-group>
                <VErrorMessage name="subInterval" as="div" class="invalid-feedback d-block" />
                <b-input-group>
                  <label
                    v-b-tooltip.hover="
                      'Total number of doses in the sequence to add to the history (max 20)'
                    "
                    >Number</label
                  >
                  <VField
                    v-slot="{ meta }"
                    v-model="form.subDose.number"
                    name="subNumber"
                    :rules="{ between: [1, 20] }"
                  >
                    <b-form-input
                      id="subNumber"
                      v-model="form.subDose.number"
                      type="number"
                      :number="true"
                      class="input-with-unit"
                      placeholder="Number of doses"
                      :state="getValidationState(meta)"
                    />
                  </VField>
                  <span class="unit-dropdown">&nbsp;</span>
                </b-input-group>
                <VErrorMessage name="subNumber" as="div" class="invalid-feedback d-block" />
              </div>
              <hr />
              <span class="flex flex-column align-content-center">
                <b-button
                  v-b-tooltip.hover="'Add these doses to the dose history'"
                  class="rounded-circle shadow bg-secondary"
                  :disabled="!formMeta.valid || !isFormValid"
                  @click="saveDoses"
                  ><PlusIcon></PlusIcon
                ></b-button>
              </span>
            </b-form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { addDoseFeedback } from '@/components/services/common';
import { isValidDateValue } from '@/models/utils/datetime';
import { DoseTypes, units } from '@/models/utils/constants.js';
import DateTimeInput from '@/components/inputs/DateTimeInput.vue';
import { createDoseTimeSeries } from '@/components/services/common.js';
import { Dose } from '@/models/Dose';
import PlusIcon from 'vue-material-design-icons/Plus.vue';
import { useDosingStore } from '@/stores/dosing';
import { useToast } from 'vue-toastification';

defineOptions({ name: 'DoseInput' });

const props = defineProps<{ model: any }>();
const emit = defineEmits<{ (e: 'switchTab', evt: any, tab: number): void }>();

const store = useDosingStore();
const visible = ref(false);
const toast = useToast();
const observer = ref<any>(null);
const fieldName = 'initial_dose';

function initForm() {
  return {
    initialDose: {
      amount: null as number | null,
      amountUnit: 'mg',
      duration: null as number | null,
      durationUnit: 'hours',
      datetime: null as Date | null,
    },
    subDose: {
      amount: null as number | null,
      amountUnit: 'mg',
      duration: null as number | null,
      durationUnit: 'hours',
      interval: null as number | null,
      intervalUnit: 'hours',
      number: null as number | null,
    },
  };
}

const form = ref(initForm());

const doseRules = computed(() => ({
  doseBetween: { unit: form.value.initialDose.amountUnit, model: props.model },
}));
const subdoseRules = computed(() => ({
  doseBetween: { unit: form.value.subDose.amountUnit, model: props.model },
}));
const durationRules = computed(() => ({
  durationBetween: { unit: form.value.initialDose.durationUnit },
}));
const subdurationRules = computed(() => ({
  durationBetween: { unit: form.value.subDose.durationUnit },
}));
const deliveryMethod = computed(() => store.getDelivery?.method);
const isFormValid = computed(
  () =>
    isValidDateValue(form.value.initialDose.datetime) &&
    ((form.value.initialDose.duration !== null && form.value.initialDose.amount !== null) ||
      (form.value.subDose.amount !== null &&
        form.value.subDose.duration !== null &&
        form.value.subDose.number !== null &&
        form.value.subDose.interval !== null)),
);

function initialDoseDateTimeValue(event: { field: string | null; value: Date }) {
  if (event.field === fieldName) {
    form.value.initialDose.datetime = event.value;
  }
}

function getValidationState(meta: { validated: boolean; valid: boolean }): boolean | null {
  return meta.validated ? meta.valid : null;
}

function saveDoses($event: any) {
  if (isFormValid.value) {
    observer.value.validate().then(({ valid }: { valid: boolean }) => {
      if (valid) {
        const dose = new Dose({
          type: DoseTypes.DOSE,
          datetime: new Date(form.value.initialDose.datetime!),
          amount: form.value.initialDose.amount,
          amountUnit: form.value.initialDose.amountUnit,
          duration: form.value.initialDose.duration ?? 1,
          durationUnit: form.value.initialDose.durationUnit,
        });
        let doses = dose.amount ? [dose] : [];
        if (form.value.subDose.number) {
          const subdose = new Dose({
            type: DoseTypes.DOSE,
            amount: form.value.subDose.amount,
            amountUnit: form.value.subDose.amountUnit,
            duration: form.value.subDose.duration ?? 1,
            durationUnit: form.value.subDose.durationUnit,
          });
          doses = createDoseTimeSeries(
            dose,
            subdose,
            form.value.subDose.number,
            form.value.subDose.interval,
            form.value.subDose.intervalUnit,
          );
        }
        const feedback = addDoseFeedback(store.addDose(doses), 'Doses added');
        emit('switchTab', $event, 1);
        toast[feedback.type](feedback.text);
      }
    });
  }
}
</script>

<style scoped></style>
